import { useState } from 'react'
import { amountToRubleInput, rubleInputToAmount } from '@/shared/lib/format'
import { Button, Dialog, Input, Select } from '@/shared/ui'
import s from './ManagerReasonPrompt.module.css'

/**
 * Что спросить перед операцией. Собирается вызывающим из СЕРВЕРНЫХ данных — здесь ничего не
 * выдумывается: подписи, факты, исход и подсказки причин приходят с сервера (3.2).
 */
export type ReasonPromptSpec = {
  title: string
  confirmLabel: string
  description?: string
  /** Разрушительная операция (отказ, отмена брони) — подтверждение не должно выглядеть обычным. */
  isReject?: boolean
  needsAmount?: boolean
  /** Из чего выбрать перед подтверждением — например, версия сценария при переводе брони. */
  options?: {
    label: string
    items: ReadonlyArray<{ value: string; label: string; help?: string }>
  }
  facts?: ReadonlyArray<{ key: string; label: string }>
  outcomeLabel?: string
  reasonSuggestions?: ReadonlyArray<string>
}

export type ReasonPromptPayload = { reason: string; amount?: number; option?: string }

type ManagerReasonPromptProps = {
  spec: ReasonPromptSpec
  isPending: boolean
  onCancel: () => void
  onConfirm: (payload: ReasonPromptPayload) => void
}

/**
 * «Почему» перед действием менеджера. Это НЕ вежливость интерфейса: причина — требование сервера,
 * без неё операция не выполняется (3.2). Раньше её подставлял промежуточный слой одной и той же
 * строкой, поэтому в истории каждой брони записано одинаковое «manager action» — требование
 * соблюдалось формально, а смысл терялся. Спрашиваем здесь и подсказываем типовыми формулировками
 * сервера, чтобы не превратить это в поле-отписку.
 *
 * Сумму спрашиваем тут же: фиксированные факты сервер дописывает сам, а сумму выдумать не может и
 * отклонит вызов без неё.
 *
 * Один диалог на три потока — действие в карточке брони (`actionsRequired`), правка живой брони
 * («вмешаться», 3.2B) и закрытие задачи из очереди: вопрос у них один и тот же, и вторая копия этой
 * формы разошлась бы с первой ровно в том, что важно, — в требовании причины. Ради третьего потока
 * компонент и живёт в `features`: очередь задач — другой виджет, а виджет виджету не сосед.
 *
 * Монтируется ТОЛЬКО пока операция выбрана (владелец даёт `key`) — поэтому набранное не может
 * протечь в следующую, и сбрасывать поля вручную в трёх местах не приходится.
 */
export function ManagerReasonPrompt({
  spec,
  isPending,
  onCancel,
  onConfirm,
}: ManagerReasonPromptProps) {
  const [reason, setReason] = useState('')
  const [rubles, setRubles] = useState('')
  // Первый вариант — разумное умолчание (у версий это самая свежая), но выбор остаётся видимым:
  // молча подставленная версия означала бы «перевели, куда сами решили».
  const [option, setOption] = useState(() => spec.options?.items[0]?.value ?? '')

  const needsAmount = spec.needsAmount === true
  const amount = needsAmount ? rubleInputToAmount(rubles) : undefined
  const chosen = spec.options?.items.find((item) => item.value === option)

  const blockedReason =
    reason.trim() === ''
      ? 'Напишите, почему — без причины операция не выполнится.'
      : needsAmount && amount === undefined
        ? 'Укажите сумму — сервер её не знает.'
        : spec.options && option === ''
          ? 'Выберите вариант.'
          : null

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
      width={480}
      title={spec.title}
      description={spec.description}
    >
      <div className={s.body}>
        {spec.options ? (
          <label className={s.field}>
            <span className={s.label}>{spec.options.label}</span>
            <Select
              aria-label={spec.options.label}
              value={option}
              options={spec.options.items.map((item) => ({ value: item.value, label: item.label }))}
              onChange={(event) => setOption(event.currentTarget.value)}
            />
            {chosen?.help ? <span className={s.optionHelp}>{chosen.help}</span> : null}
          </label>
        ) : null}

        {needsAmount ? (
          <label className={s.field}>
            <span className={s.label}>Сумма</span>
            <span className={s.amountRow}>
              {/* `text` + `inputMode`, а не `type="number"`: числовое поле отдаёт пустую строку на
                  запятую, то есть на русской раскладке «1 500,50» молча превращалось бы в «ничего
                  не введено». Тот же приём, что в форме новой брони. */}
              <Input
                aria-label="Сумма в рублях"
                inputMode="decimal"
                value={rubles}
                onChange={(event) => setRubles(event.currentTarget.value)}
              />
              <span className={s.unit}>₽</span>
            </span>
          </label>
        ) : null}

        <label className={s.field}>
          <span className={s.label}>Почему</span>
          <Input
            aria-label="Почему"
            value={reason}
            placeholder="Коротко: что произошло"
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        </label>

        {spec.reasonSuggestions && spec.reasonSuggestions.length > 0 ? (
          <div className={s.suggestions}>
            {spec.reasonSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className={s.suggestion}
                // ДОПИСЫВАЕМ к набранному, а не затираем: подсказка — быстрый старт формулировки,
                // и потерять из-за неё уже написанное человек не должен.
                onClick={() =>
                  setReason((current) =>
                    current.trim() === '' ? suggestion : `${current.trim()}, ${suggestion}`,
                  )
                }
              >
                {suggestion}
              </button>
            ))}
          </div>
        ) : null}

        {/* Что уедет вместе с нажатием и куда пойдёт бронь. У ручного подтверждения оплаты это факт
            «деньги действительно пришли», по которому отмена брони выбирает между возвратом и
            отменой счёта, — менеджеру стоит видеть, что именно он утверждает (3.2). */}
        {(spec.facts && spec.facts.length > 0) || spec.outcomeLabel ? (
          <p className={s.facts}>
            {spec.facts && spec.facts.length > 0
              ? `Будет записано: ${spec.facts
                  .map((fact) =>
                    fact.key === 'amount.manual' && amount !== undefined
                      ? `${fact.label} — ${amountToRubleInput(amount)} ₽`
                      : fact.label,
                  )
                  .join(', ')}.`
              : null}
            {spec.outcomeLabel ? ` Дальше бронь пойдёт по ветке «${spec.outcomeLabel}».` : null}
          </p>
        ) : null}

        {blockedReason ? <p className={s.blocked}>{blockedReason}</p> : null}
      </div>

      <div className={s.foot}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Отмена
        </Button>
        <Button
          variant={spec.isReject ? 'secondary' : 'accent'}
          className={spec.isReject ? s.rejectConfirm : undefined}
          size="sm"
          disabled={blockedReason !== null || isPending}
          onClick={() =>
            onConfirm({
              reason: reason.trim(),
              ...(amount === undefined ? {} : { amount }),
              ...(spec.options ? { option } : {}),
            })
          }
        >
          {isPending ? 'Отправляем...' : spec.confirmLabel}
        </Button>
      </div>
    </Dialog>
  )
}
